// contexts/ToastContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import ToastClose from '@assets/svg/ToastClose.svg?react';
import ToastError from '@assets/svg/ToastError.svg?react';
import ToastSuccess from '@assets/svg/ToastSuccess.svg?react';
import {
  ToastContainer,
  ToastContent,
  ToastMessage,
  ToastCloseButton,
  ToastIcon,
  SnackbarContainer,
  SnackbarWrapper,
  IconSvg
} from './toast.styles';

interface ToastContextType {
  showToast: (message: string, options?: ToastOptions) => void;
}

interface ToastOptions {
  severity?: 'error' | 'success' | 'warning';
  duration?: number;
}

const ToastContext = createContext<ToastContextType>({} as ToastContextType);

export const ToastProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'error' | 'success' | 'warning'>('error');
  const [duration, setDuration] = useState(3000);
  
  const lang = localStorage.getItem('i18nextLng') || 'ar';
  const isAr = lang === 'ar';

  const showToast = (msg: string, options?: ToastOptions) => {
    setMessage(msg);
    setSeverity(options?.severity || 'error');
    setDuration(options?.duration || 3000); 
    setOpen(true);
  };

  const hideToast = () => setOpen(false);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setOpen(false);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [open, duration]);

  const getMinWidth = () => {
    return message.length > 60 ? '700px' : '485px';
  };

  const ToastContentComponent = () => (
    <ToastContainer $isAr={isAr} $minWidth={getMinWidth()}>
      <ToastContent $isAr={isAr}>
        <ToastIcon>
          <IconSvg>
            {severity === 'success' ? 
              <ToastSuccess /> : 
              <ToastError />
            }
          </IconSvg>
        </ToastIcon>
        <ToastMessage>
          {message}
        </ToastMessage>
      </ToastContent>

      <ToastCloseButton onClick={hideToast}>
        <ToastClose />
      </ToastCloseButton>
    </ToastContainer>
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {open && (
        <SnackbarContainer>
          <SnackbarWrapper $isVisible={open}>
            <ToastContentComponent />
          </SnackbarWrapper>
        </SnackbarContainer>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);